import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { atom_mobileListHeight } from '@app/shared/state/list-mode.atom';

interface UseResizablePanelConfig {
  minHeight: number;
  maxHeight: number;
  initialHeight: number;
  initiallyVisible?: boolean;
  onDragStart?: () => void;
  onDragEnd?: (finalHeight: number) => void;
}

interface UseResizablePanelReturn {
  currentHeight: number;
  setCurrentHeight: (h: number) => void;
  isDragging: boolean;
  isMaxHeight: boolean;
  setIsMaxHeight: (v: boolean) => void;
  isMounted: boolean;
  startExit: (onComplete?: () => void) => void;
  dragHandleProps: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    ref: React.RefObject<HTMLDivElement>;
  };
  containerTransitionStyle: {
    transition: string;
    willChange: string;
    transform: string;
  };
}

export function useResizablePanel(config: UseResizablePanelConfig): UseResizablePanelReturn {
  const { minHeight, maxHeight, initialHeight, onDragStart, onDragEnd } = config;

  const [currentHeight, setCurrentHeight] = useState<number>(initialHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [isMaxHeight, setIsMaxHeight] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Slide-up animation on mount
  // When initiallyVisible, skip animation (used for exiting instances in cross-fade)
  useEffect(() => {
    if (config.initiallyVisible) {
      setIsMounted(true);
      return;
    }
    // Single rAF: lets browser paint initial translateY(100%) state, then animate
    requestAnimationFrame(() => {
      setIsMounted(true);
    });
  }, []);

  // Slide-down exit animation - call this before unmounting
  const startExit = useCallback((onComplete?: () => void) => {
    setIsExiting(true);
    setTimeout(() => {
      try {
        onComplete?.();
      } catch (e) {
        console.error('[useResizablePanel] startExit callback error:', e);
      }
    }, 280); // slightly before transition ends to avoid visual gap
  }, []);

  const touchStartYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  const currentHeightRef = useRef<number>(currentHeight);
  const handleRef = useRef<HTMLDivElement>(null);

  // Keep ref in sync with state
  useEffect(() => {
    currentHeightRef.current = currentHeight;
  }, [currentHeight]);

  // Sync height to atom (skip for exiting instances to avoid conflicts)
  const setMobileListHeight = useSetRecoilState(atom_mobileListHeight);

  useEffect(() => {
    if (config.initiallyVisible) return;
    console.log('[FIT-DEBUG] mobileListHeight WRITE:', currentHeight, 'at', Date.now());
    setMobileListHeight(currentHeight);
  }, [currentHeight, setMobileListHeight]);

  // Reset atom on unmount
  useEffect(() => {
    if (config.initiallyVisible) return;
    return () => {
      console.log('[FIT-DEBUG] mobileListHeight UNMOUNT-RESET to 0 at', Date.now());
      setMobileListHeight(0);
    };
  }, [setMobileListHeight]);

  // Core drag handlers
  const handleDragStart = useCallback((clientY: number) => {
    touchStartYRef.current = clientY;
    startHeightRef.current = currentHeightRef.current;
    setIsDragging(true);
    document.body.classList.add('resizing');
    onDragStart?.();
  }, [onDragStart]);

  const handleDragMove = useCallback((clientY: number) => {
    if (!isDragging) return;
    const deltaY = touchStartYRef.current - clientY;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeightRef.current + deltaY));
    currentHeightRef.current = newHeight;

    // Update DOM directly during drag — no state update, no re-render
    const container = handleRef.current?.parentElement;
    if (container) {
      container.style.height = `${newHeight}px`;
    }

    if (newHeight >= maxHeight - 10) {
      setIsMaxHeight(true);
    } else if (newHeight <= minHeight + 10) {
      setIsMaxHeight(false);
    }
  }, [isDragging, minHeight, maxHeight]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    setCurrentHeight(currentHeightRef.current); // sync state once at the end
    document.body.classList.remove('resizing');
    onDragEnd?.(currentHeightRef.current);
  }, [isDragging, onDragEnd]);

  // Touch event handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    handleDragStart(e.touches[0].clientY);
  }, [handleDragStart]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    handleDragMove(e.touches[0].clientY);
  }, [handleDragMove]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    handleDragEnd();
  }, [handleDragEnd]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleDragStart(e.clientY);
  }, [handleDragStart]);

  // Global mouse/touch listeners when dragging
  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleDragMove(e.clientY);
    };

    const onMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleDragEnd();
    };

    const onGlobalTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleDragMove(e.touches[0].clientY);
    };

    const onGlobalTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleDragEnd();
    };

    document.addEventListener('mousemove', onMouseMove, { capture: true });
    document.addEventListener('mouseup', onMouseUp, { capture: true });
    document.addEventListener('touchmove', onGlobalTouchMove, { passive: false, capture: true });
    document.addEventListener('touchend', onGlobalTouchEnd, { capture: true });

    return () => {
      document.removeEventListener('mousemove', onMouseMove, { capture: true });
      document.removeEventListener('mouseup', onMouseUp, { capture: true });
      document.removeEventListener('touchmove', onGlobalTouchMove, { capture: true } as EventListenerOptions);
      document.removeEventListener('touchend', onGlobalTouchEnd, { capture: true });
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const isVisible = isMounted && !isExiting;

  return {
    currentHeight,
    setCurrentHeight,
    isDragging,
    isMaxHeight,
    setIsMaxHeight,
    isMounted: isVisible,
    startExit,
    dragHandleProps: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
      ref: handleRef,
    },
    containerTransitionStyle: {
      transition: isDragging ? 'none' : 'height 0.15s ease-out, transform 0.3s ease-out',
      willChange: isDragging ? 'height' : 'auto',
      transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
    },
  };
}
